/**
 * Replaces numerical segments in a URL path with the placeholder `{id}` so
 * errors that vary only by entity ID get grouped. Numbers immediately after
 * `/api/` are preserved (they are API versions, not IDs). Query parameter
 * values that are purely numeric are also replaced.
 *
 * Same-origin and relative URLs are reduced to path + query so they group
 * across hosts; cross-origin URLs keep their scheme + host so third-party
 * endpoints stay distinguishable from our own API paths.
 *
 * Example:
 *   /api/3/media2/85994393674/view?user=123 -> /api/3/media2/{id}/view?user={id}
 */
export const generalizeUrl = (url: string, preserveQueryParams: readonly string[] = []): string => {
	if (!url) return "";

	let urlObj: URL;
	try {
		urlObj = new URL(url, "http://dummy.com/");
	} catch {
		return url;
	}

	let originPrefix = "";
	const isProtocolRelative = url.startsWith("//");
	const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(url);
	if (urlObj.host && (hasScheme || isProtocolRelative)) {
		const loc = typeof window !== "undefined" ? window.location : undefined;
		if (isProtocolRelative) {
			if (urlObj.host !== loc?.host) originPrefix = `//${urlObj.host}`;
		} else if (urlObj.origin !== loc?.origin) {
			originPrefix = `${urlObj.protocol}//${urlObj.host}`;
		}
	}

	const originalSegments = urlObj.pathname.split("/");
	const generalizedSegments = originalSegments.map((segment, index) => {
		if (segment === "" || !/^\d+$/.test(segment)) return segment;
		if (originalSegments[index - 1]?.toLowerCase() === "api") return segment;
		return "{id}";
	});

	const generalizedPath = generalizedSegments.join("/") || "/";

	const generalizedSearch = new URLSearchParams();
	for (const [key, value] of urlObj.searchParams.entries()) {
		generalizedSearch.append(
			key,
			!preserveQueryParams.includes(key) && /^\d+$/.test(value) ? "{id}" : value
		);
	}
	const search = generalizedSearch.toString();

	const combined = originPrefix + generalizedPath + (search ? "?" + search : "");
	try {
		return decodeURIComponent(combined);
	} catch {
		return combined;
	}
};
